"use client"

import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  CalculatorCtaBanner,
  useMembershipDetailsHref,
} from "@/components/tools/calculator-closing-cta"
import { BookingCta } from "@/components/ui/booking-cta"
import { FaqSection, type FaqAccordionItem } from "@/components/ui/faq-section"
import { ArrowRightIcon, buttonVariants } from "@/components/ui/button"
import {
  ArrowPathIcon,
  BodyFigureIcon,
  DropletIcon,
  ExerciseIcon,
  GlobeIcon,
  GreenCheck,
  InfoIcon,
  PersonIcon,
  ScaleIcon,
} from "@/components/ui/icons"
import { bmiCalculatorKeys as k } from "@/i18n/bmi-calculator-keys"
import { useCountry } from "@/components/layout/country-provider"
import { baseBloodTest } from "@/lib/base-blood-test"
import { isAllowedInCountry } from "@/lib/country-gates"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { cn } from "@/lib/utils"
import { OtherToolsSection } from "../../../_components/other-tools-section"
import { CATEGORY_ORDER } from "./bmi"
import { BmiCalculatorWidget, CATEGORY_STYLES } from "./bmi-calculator-widget"

/**
 * Icons are pre-built elements rather than component references: the shared
 * `icons.tsx` set is split between a `width`/`height` prop shape and a `size`
 * one, so a single `<Icon width={28} …>` call site can't cover both. Building
 * the element here keeps each icon called the way it's declared.
 */
const ICON_CLASS = "text-aubergine-800"

const LIMITS = [
  {
    titleKey: k.limit1Title,
    bodyKey: k.limit1Body,
    icon: <ScaleIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.limit2Title,
    bodyKey: k.limit2Body,
    icon: <BodyFigureIcon size={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.limit3Title,
    bodyKey: k.limit3Body,
    icon: <GlobeIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.limit4Title,
    bodyKey: k.limit4Body,
    icon: <PersonIcon width={28} height={28} className={ICON_CLASS} />,
  },
]

const BEYOND = [
  {
    titleKey: k.beyond1Title,
    bodyKey: k.beyond1Body,
    icon: <BodyFigureIcon size={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.beyond2Title,
    bodyKey: k.beyond2Body,
    icon: <ExerciseIcon size={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.beyond3Title,
    bodyKey: k.beyond3Body,
    icon: <DropletIcon width={28} height={28} className={ICON_CLASS} />,
  },
  {
    titleKey: k.beyond4Title,
    bodyKey: k.beyond4Body,
    icon: <ArrowPathIcon size={28} className={ICON_CLASS} />,
  },
]

const MEMBERSHIP_FEATURES = [
  k.memFeature1,
  k.memFeature2,
  k.memFeature3,
  k.memFeature4,
  k.memFeature5,
]

const SECTION = "py-16 md:py-24"
const CONTAINER = "mx-auto max-w-[1440px] px-4 md:px-10"
/** Prose sections read at a narrower measure than the 1440 grid. */
const PROSE = "mx-auto max-w-[760px]"

export default function BmiCalculatorClient({
  initialFaqs = [],
}: {
  initialFaqs?: FaqAccordionItem[]
}) {
  const t = useTranslations("bmiCalculator")
  const lp = useLocalePath()
  const membershipDetailsHref = useMembershipDetailsHref()
  const { code: country } = useCountry()

  /**
   * Which promise the closing panel is allowed to make, read from the registry
   * rather than compared against a country literal.
   *
   * `page:membership` is the same gate the panel's own secondary link points
   * at, so this cannot drift from it: a market without that page cannot be
   * shown a link into it. NL is that market — it sells no membership at all
   * (AHD-9361, "no page, no promise") — so there the panel points at the
   * advanced blood test, the product this market does sell, and at /pricing.
   * The copy follows the link: the `mem*` keys carry check-up wording in
   * messages/nl-NL.json and messages/en-NL.json rather than membership wording.
   */
  const hasMembership = isAllowedInCountry("page:membership", country)

  const faqFallback: FaqAccordionItem[] = [
    { question: t(k.faqQ1), answer: t(k.faqA1) },
    { question: t(k.faqQ2), answer: t(k.faqA2) },
    { question: t(k.faqQ3), answer: t(k.faqA3) },
    { question: t(k.faqQ4), answer: t(k.faqA4) },
    { question: t(k.faqQ5), answer: t(k.faqA5) },
    { question: t(k.faqQ6), answer: t(k.faqA6) },
  ]

  return (
    <>
      {/* ─────────────── Hero + calculator ─────────────── */}
      <section className="pt-8 pb-16 md:pt-12 md:pb-24">
        <div className={CONTAINER}>
          {/* The widget owns its own layout — one centred column, with the
              result panel appearing under the form once Calculate has run —
              because the form and the result share state; the intro rides
              along in it so the h1 stays visible in this file. */}
          <BmiCalculatorWidget
            intro={
              <>
                <h1 className="type-display-lg text-balance text-neutral-900">
                  {t(k.heading)}
                </h1>
                <p className="type-body-lg mt-5 text-neutral-600">
                  {t(k.subheading)}
                </p>
              </>
            }
          />
        </div>
      </section>

      {/* ─────────────── What is BMI ─────────────── */}
      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className={PROSE}>
            <p className="type-label-lg text-neutral-400">{t(k.whatLabel)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.whatHeading)}
            </h2>
            <div className="mt-6 space-y-5">
              <p className="type-body-lg text-neutral-600">{t(k.whatP1)}</p>
              <p className="type-body-lg text-neutral-600">{t(k.whatP2)}</p>
              <p className="type-body-lg text-neutral-600">{t(k.whatP3)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── Formula ─────────────── */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <div className={PROSE}>
            <h2 className="type-display-sm text-neutral-900">
              {t(k.formulaHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.formulaIntro)}
            </p>
            {/* No card title: the h2 above already says what this is, and
                there is only one formula to show. */}
            <div className="mt-8 rounded-[16px] bg-white p-6">
              <p className="type-body-md font-mono text-neutral-900">
                {t(k.formulaMetricValue)}
              </p>
              <p className="type-body-md mt-4 text-neutral-600">
                {t(k.formulaMetricExample)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── Category reference table ─────────────── */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <div className={PROSE}>
            <h2 className="type-display-sm text-neutral-900">
              {t(k.tableHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.tableIntro)}
            </p>
          </div>

          {/* Wide table scrolls inside its own container on narrow viewports. */}
          <div className={cn(PROSE, "mt-8 overflow-x-auto")}>
            <table
              data-testid="bmi-category-table"
              className="w-full min-w-[640px] border-collapse text-left"
            >
              <caption className="sr-only">{t(k.tableHeading)}</caption>
              <thead>
                <tr className="border-b border-neutral-200">
                  <th
                    scope="col"
                    className="type-label-md pb-3 text-neutral-500 normal-case"
                  >
                    {t(k.tableColCategory)}
                  </th>
                  <th
                    scope="col"
                    className="type-label-md pb-3 text-neutral-500 normal-case"
                  >
                    {t(k.tableColRange)}
                  </th>
                  <th
                    scope="col"
                    className="type-label-md pb-3 text-neutral-500 normal-case"
                  >
                    {t(k.tableColMeaning)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {CATEGORY_ORDER.map((category) => (
                  <tr
                    key={category.id}
                    className="border-b border-neutral-200 align-top last:border-0"
                  >
                    <th scope="row" className="py-4 pr-4">
                      <span
                        className={cn(
                          "type-title-sm inline-block rounded-full px-3 py-1 whitespace-nowrap",
                          CATEGORY_STYLES[category.id].chip
                        )}
                      >
                        {t(category.labelKey)}
                      </span>
                    </th>
                    <td className="type-body-md py-4 pr-4 whitespace-nowrap text-neutral-900 tabular-nums">
                      {t(category.rangeKey)}
                    </td>
                    <td className="type-body-md py-4 text-neutral-600">
                      {t(category.meaningKey)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className={cn(PROSE, "type-body-sm mt-4 text-neutral-500")}>
            {t(k.tableNote)}
          </p>
        </div>
      </section>

      {/* ─────────────── Limitations ─────────────── */}
      <section className={cn(SECTION, "bg-neutral-100")}>
        <div className={CONTAINER}>
          <div className={PROSE}>
            <p className="type-label-lg text-neutral-400">{t(k.limitsLabel)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.limitsHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.limitsIntro)}
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-[1128px] grid-cols-1 gap-4 md:grid-cols-2">
            {LIMITS.map(({ titleKey, bodyKey, icon }) => (
              <div key={titleKey} className="rounded-[16px] bg-white p-6">
                {icon}
                <h3 className="type-title-lg mt-4 text-neutral-900">
                  {t(titleKey)}
                </h3>
                <p className="type-body-md mt-3 text-neutral-600">
                  {t(bodyKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── Waist measurement ─────────────── */}
      <section className={SECTION}>
        <div className={CONTAINER}>
          <div className={PROSE}>
            <h2 className="type-display-sm text-neutral-900">
              {t(k.waistHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.waistBody)}
            </p>
            <div className="mt-8 flex items-start gap-4 rounded-[16px] bg-white p-6">
              <InfoIcon
                size={24}
                className="text-aubergine-800 mt-0.5 shrink-0"
              />
              <div>
                <h3 className="type-title-md text-neutral-900">
                  {t(k.waistTipTitle)}
                </h3>
                <p className="type-body-md mt-2 text-neutral-600">
                  {t(k.waistTipBody)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── Beyond BMI ─────────────── */}
      <section className="pb-16 md:pb-24">
        <div className={CONTAINER}>
          <div className={PROSE}>
            <p className="type-label-lg text-neutral-400">{t(k.beyondLabel)}</p>
            <h2 className="type-display-sm mt-4 text-neutral-900">
              {t(k.beyondHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.beyondIntro)}
            </p>
          </div>
          <div className="mx-auto mt-10 grid max-w-[1128px] grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {BEYOND.map(({ titleKey, bodyKey, icon }) => (
              <div key={titleKey} className="rounded-[16px] bg-white p-6">
                {icon}
                <h3 className="type-title-md mt-4 text-neutral-900">
                  {t(titleKey)}
                </h3>
                <p className="type-body-md mt-3 text-neutral-600">
                  {t(bodyKey)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────── Book a membership ─────────────── */}
      <section
        className={SECTION}
        id="membership"
        data-testid="bmi-membership-section"
      >
        <div className={CONTAINER}>
          <div className="bg-aubergine-800 overflow-hidden rounded-[16px] px-6 py-12 md:px-12 md:py-16">
            <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-2 lg:gap-16">
              <div>
                <p className="type-label-lg text-aubergine-300">
                  {t(k.memLabel)}
                </p>
                <h2 className="type-display-sm mt-4 text-balance text-white">
                  {t(k.memHeading)}
                </h2>
                <p className="type-body-lg text-aubergine-100 mt-5">
                  {t(k.memBody)}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  {hasMembership ? (
                    <>
                      <BookingCta
                        forMembership
                        // `tonal` on purpose: this CTA sits on the aubergine
                        // panel, where `primary` would be aubergine-on-aubergine
                        // and stop reading as a button.
                        className={cn(
                          buttonVariants({ variant: "tonal" }),
                          "group"
                        )}
                        trackingLabel="bmi-calculator-membership-cta"
                        trackingLocation="tools/bmi-calculator"
                      >
                        {t(k.memCtaPrimary)}
                        <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-out group-hover:translate-x-1" />
                      </BookingCta>
                      <Link
                        href={membershipDetailsHref}
                        className="type-title-md text-white underline underline-offset-4 transition-opacity hover:opacity-80"
                      >
                        {t(k.memCtaSecondary)}
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        href={lp(`/tests/${baseBloodTest(country).slug}`)}
                        data-testid="bmi-checkup-cta"
                        className={cn(
                          buttonVariants({ variant: "tonal" }),
                          "group"
                        )}
                      >
                        {t(k.memCtaPrimary)}
                        <ArrowRightIcon className="ml-2 transition-transform duration-200 ease-out group-hover:translate-x-1" />
                      </Link>
                      <Link
                        href={lp("/pricing")}
                        className="type-title-md text-white underline underline-offset-4 transition-opacity hover:opacity-80"
                      >
                        {t(k.memCtaSecondary)}
                      </Link>
                    </>
                  )}
                </div>
                <p className="type-body-sm text-aubergine-300 mt-6">
                  {t(k.memFootnote)}
                </p>
              </div>

              <ul className="space-y-4">
                {MEMBERSHIP_FEATURES.map((key) => (
                  <li
                    key={key}
                    className="type-body-lg text-aubergine-100 flex items-start gap-3"
                  >
                    <GreenCheck
                      width={22}
                      height={22}
                      className="mt-0.5 shrink-0"
                    />
                    <span>{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── FAQ ─────────────── */}
      <FaqSection
        heading={t(k.faqHeading)}
        items={initialFaqs.length > 0 ? initialFaqs : faqFallback}
        linkLabel={null}
        allowMultiple
        currentPage="tools/bmi-calculator"
      />

      {/* ─────────────── Other calculators ─────────────── */}
      {/* Lists the other `/tools/*` calculators this market has, from the
          registry in `lib/health-tools.ts`, and renders nothing where there
          are none. Placed after the FAQ and before the closing CTA: a visitor
          who read the page is the one worth cross-linking, and it should not
          sit between the result and the conversion path. */}
      <OtherToolsSection currentToolId="bmi-calculator" />

      {/* ─────────────── Closing CTA ─────────────── */}
      <CalculatorCtaBanner
        tool="bmi"
        title={t(k.ctaHeading)}
        subtitle={t(k.ctaBody)}
        primaryLabel={t(k.ctaPrimary)}
        secondaryLabel={t(k.ctaSecondary)}
      />

      {/* ─────────────── Medical disclaimer ─────────────── */}
      <section className="pt-10 pb-16">
        <div className={CONTAINER}>
          <p className={cn(PROSE, "type-body-sm text-neutral-500")}>
            {t(k.disclaimer)}
          </p>
        </div>
      </section>
    </>
  )
}
